import React, { useState } from 'react';
import { Auth } from '@aws-amplify/auth';
import { useLocation } from 'react-router-dom';
import { Stack, Text, Box } from '@tonic-ui/react';
import { useTranslation } from 'react-i18next';

import filterMessage from '../../utils/message-filter';
import NotificationBanner from '../../components/notification-banner/notification-banner';
import validateUserAccount from '../../utils/validate-user-account';

import {
  FormContainer,
  InputField,
  forgotPasswordValidation,
  SubmitButton,
} from '../../components/forms';
import useQueryParams from '../../utils/query-params';
import RouteLink from '../../components/route-link';
import CenteredLayout from '../../components/centered-layout';

type ForgotPasswordProps = {
  email: string;
  setSubmitting: (value: boolean) => void;
  resetForm: () => void;
};

const ForgotPassword = (): JSX.Element => {
  const [formErrors, setFormErrors] = useState({
    error: '',
  });
  const [passwordResetSuccess, setPasswordResetSuccess] = useState(false);
  const { t } = useTranslation();
  const location = useLocation();
  const { email: confirmationEmail = '' } = useQueryParams<AuthQueryParams>(location);

  const validateNewRegistration = async () => {
    try {
      return await validateUserAccount(location.search);
    } catch (error) {
      setFormErrors({
        ...formErrors,
        error: filterMessage(error),
      });
    }
  };

  const handleSubmit = async ({ email, setSubmitting, resetForm }: ForgotPasswordProps) => {
    setFormErrors({
      ...formErrors,
      error: '',
    });

    // Validate the user email
    if (confirmationEmail) {
      await validateNewRegistration();
    }

    try {
      await Auth.forgotPassword(email);
      setSubmitting(false);
      resetForm();
      setPasswordResetSuccess(true);
    } catch (error) {
      const errMsg = !error.message ? error : error.message;
      console.error('Error: ', errMsg);

      setFormErrors({
        ...formErrors,
        error: filterMessage(error),
      });

      setSubmitting(false);
    }
  };

  const defaultValues: { email: string } = {
    email: '',
  };

  return (
    <CenteredLayout>
      {passwordResetSuccess ? (
        <>
          <Stack spacing="8x" mt="32px">
            <Text size="xl" lineHeight="xl">
              {t('passwordReset.confirmation.title')}
            </Text>
            <Text size="md" lineHeight="lg">
              {t('passwordReset.confirmation.message')}
            </Text>
            <Box textAlign="center" mt={32}>
              <RouteLink href="/">{t('goBack')}</RouteLink>
            </Box>
          </Stack>
        </>
      ) : (
        <FormContainer
          id="forgot-password-form"
          customValidation={forgotPasswordValidation}
          initialValues={defaultValues}
          submitAction={(formPayload) => handleSubmit(formPayload)}
        >
          <Stack spacing="8x">
            <Text size="xl" lineHeight="xl">
              {t('passwordReset.title')}
            </Text>
            {formErrors?.error && (
              <NotificationBanner
                name="forgot-password-form"
                severity="error"
                variant="outline"
                shouldOpen={true}
                bannerText={formErrors.error}
              />
            )}
            <Text size="md" lineHeight="lg">
              {t('passwordReset.message')}
            </Text>
            <Box>
              <InputField
                id="email"
                type="email"
                label={t('email')}
                name="email"
                isRequired={true}
              />
            </Box>
            <SubmitButton label={t('passwordReset.sendEmail')} id="forgot-password-submit-btn" />
            <Box textAlign="center">
              <RouteLink href="/">{t('goBack')}</RouteLink>
            </Box>
          </Stack>
        </FormContainer>
      )}
    </CenteredLayout>
  );
};

export default ForgotPassword;
